// Cascade Provider→Model picker per service_key di platform_model_config
// (Task #1305 + #1446 + #1463). Spostato in @easyflow/ui-ai-routing per riuso
// master. apiClient e notify sono iniettati come props per evitare deps
// dirette su `@/lib/api` e `@/hooks/use-toast`.
import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { Loader2, Sparkles, AlertTriangle, Wand2, RefreshCw } from 'lucide-react';
import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TooltipProvider,
  Tooltip as UITooltip,
  TooltipTrigger,
  TooltipContent,
} from '@easyflow/ui-frontend';
import type { ApiClientLike, NotifyFn } from './types';

export interface ServiceModelPickerProps {
  platformServiceKey: string;
  provider: string | null;
  label?: string;
  widthPx?: number;
  apiClient: ApiClientLike;
  notify: NotifyFn;
  /** Endpoint recommender (default `/admin/models/recommend`). */
  recommendEndpoint?: string;
  /** Endpoint lista config piattaforma (default `/master/platform-models/`). */
  platformConfigEndpoint?: string;
  /** Builder endpoint update modello (default `${platformConfigEndpoint}${key}`). */
  buildSaveEndpoint?: (serviceKey: string) => string;
  /**
   * Task #2016 — base path discovery catalog (POST). Il picker chiama
   * `${discoverEndpointBase}/${provider}` (recovery deterministico
   * per-provider, NON discovery broad cross-provider). Default
   * `/admin/models/discover`.
   */
  discoverEndpoint?: string;
  /**
   * Modalità sola lettura: skippa recommend fetch + auto-save + discovery,
   * legge solo il `current_model` da `platformConfigEndpoint` e mostra un
   * chip readonly con badge `platform`. Usato lato pannello tenant per i
   * service non instradabili per tenant (`tenant_routable=false` in
   * `SERVICE_CAPABILITY_MAP`, oggi solo `embedding`), il cui PUT verrebbe
   * rifiutato dal backend con 403 `SERVICE_PLATFORM_SCOPED`.
   */
  readOnly?: boolean;
  /** Tooltip mostrato sul chip readonly. */
  readOnlyReason?: string;
}

interface BreakdownDims {
  quality: number;
  cost: number;
  latency: number;
  feature_match: number;
}

interface ScoredEntry {
  provider: string;
  model_id: string;
  display_name: string | null;
  score: number;
  motivation: string;
  is_new: boolean;
  is_deprecated: boolean;
  metadata_complete: boolean;
  breakdown: BreakdownDims;
}

interface RecommendResponse {
  service_key: string;
  capability: string;
  top: ScoredEntry[];
  others: ScoredEntry[];
}

interface PlatformModelRow {
  service_key: string;
  current_model: string;
  provider: string;
}

interface ApiEnvelope<T> {
  data?: T;
}

const NORMALIZE_PROVIDER: Record<string, string> = {
  google:  'google',
  gemini:  'google',
  openai:  'openai',
  claude:  'claude',
};

/**
 * Risolve il provider grezzo in `{ provider_id, endpoint_id? }` per query
 * recommend. Supporta i 3 canonici (openai/google/claude) e i custom
 * tenant-scoped nel formato `custom:<uuid>` (Task #2015/#2016).
 *
 * Per `custom:<uuid>` ritorna `provider_id='custom'` + `endpoint_id=<uuid>`
 * così il recommender vincola il catalog all'endpoint custom selezionato
 * (no cross-endpoint leak).
 */
function resolveRecommendProvider(provider: string | null | undefined): {
  provider_id: string | null;
  endpoint_id: string | null;
} {
  if (!provider) return { provider_id: null, endpoint_id: null };
  if (provider.startsWith('custom:')) {
    const uuid = provider.slice('custom:'.length).trim();
    return { provider_id: 'custom', endpoint_id: uuid || null };
  }
  return { provider_id: NORMALIZE_PROVIDER[provider] ?? null, endpoint_id: null };
}

export function ServiceModelPicker({
  platformServiceKey,
  provider,
  label,
  widthPx = 240,
  apiClient,
  notify,
  recommendEndpoint = '/admin/models/recommend',
  platformConfigEndpoint = '/master/platform-models/',
  buildSaveEndpoint,
  discoverEndpoint = '/admin/models/discover',
  readOnly = false,
  readOnlyReason,
}: ServiceModelPickerProps) {
  const [reco, setReco] = useState<RecommendResponse | null>(null);
  const [current, setCurrent] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [discovering, setDiscovering] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Task #2139 — guard anti-loop refetch:
  //  - AbortController per cancellare la fetch in flight su unmount / change deps;
  //  - attemptsRef tiene il conteggio retry per la combinazione corrente
  //    (platformServiceKey + provider + readOnly). Reset ad ogni cambio key.
  //  - max 2 retry con backoff esponenziale (500ms, 1500ms). Dopo che la
  //    combinazione corrente fallisce 3 volte (1 + 2 retry) lo stato resta
  //    `error` inerte finché l'utente non clicca "Riprova" (handleRetry).
  const abortRef = useRef<AbortController | null>(null);
  const attemptsRef = useRef<number>(0);
  const lastKeyRef = useRef<string>('');

  const fetchAllInternal = useCallback(async (signal: AbortSignal): Promise<void> => {
    // Modalità readonly (es. tenant su service platform-scoped): leggo solo
    // il `current_model` da `platformConfigEndpoint`, NON chiamo recommend e
    // NON auto-salvo top-1. Il PUT verrebbe rifiutato dal backend con 403.
    if (readOnly) {
      const cfgRes = await apiClient.get<ApiEnvelope<PlatformModelRow[]> | PlatformModelRow[]>(platformConfigEndpoint, { signal });
      if (signal.aborted) return;
      const cfgList = unwrap<PlatformModelRow[]>(cfgRes.data);
      const row = Array.isArray(cfgList)
        ? cfgList.find((r) => r.service_key === platformServiceKey)
        : null;
      setCurrent(row?.current_model ?? null);
      return;
    }

    const params = new URLSearchParams({ service_key: platformServiceKey, top_n: '3' });
    const { provider_id, endpoint_id } = resolveRecommendProvider(provider);
    if (provider_id) params.set('provider_id', provider_id);
    if (endpoint_id) params.set('endpoint_id', endpoint_id);

    const [recoRes, cfgRes] = await Promise.all([
      apiClient.get<ApiEnvelope<RecommendResponse> | RecommendResponse>(`${recommendEndpoint}?${params.toString()}`, { signal }),
      apiClient.get<ApiEnvelope<PlatformModelRow[]> | PlatformModelRow[]>(platformConfigEndpoint, { signal }),
    ]);
    if (signal.aborted) return;

    const recoBody = unwrap<RecommendResponse>(recoRes.data);
    setReco(recoBody);

    const cfgList = unwrap<PlatformModelRow[]>(cfgRes.data);
    const row = Array.isArray(cfgList)
      ? cfgList.find((r) => r.service_key === platformServiceKey)
      : null;
    const curModel = row?.current_model ?? null;
    setCurrent(curModel);

    // Task #2016 — auto-select top-1 quando il servizio non ha ancora un
    // modello salvato (current === null). Evita lo stato "Seleziona modello…"
    // che bloccherebbe il salvataggio implicito. Persiste subito così la
    // pipeline ha un default lavorabile senza intervento operatore.
    if (!curModel && recoBody.top.length > 0) {
      const top1 = recoBody.top[0];
      if (top1 && provider && !signal.aborted) {
        try {
          const url = buildSaveEndpoint
            ? buildSaveEndpoint(platformServiceKey)
            : `${platformConfigEndpoint}${platformServiceKey}`;
          // Task #2104 — payload canonico `{ provider, model }`: provider
          // esplicito evita la derivazione legacy backend (rerank_secondary
          // fail 400 NO_PROVIDER_SET / MODEL_NOT_IN_CATALOG).
          await apiClient.put(url, { provider, model: top1.model_id });
          if (!signal.aborted) setCurrent(top1.model_id);
        } catch {
          /* fail-soft: il select resta su placeholder, l'utente può scegliere a mano */
        }
      }
    }
  }, [platformServiceKey, provider, apiClient, recommendEndpoint, platformConfigEndpoint, buildSaveEndpoint, readOnly]);

  const runFetch = useCallback(async (signal: AbortSignal, isRetry: boolean): Promise<void> => {
    if (!isRetry) setError(null);
    setLoading(true);
    try {
      await fetchAllInternal(signal);
      if (!signal.aborted) {
        setError(null);
        attemptsRef.current = 0;
      }
    } catch (err: unknown) {
      if (signal.aborted) return;
      const isAbort = (err as { name?: string })?.name === 'AbortError' || (err as { name?: string })?.name === 'CanceledError';
      if (isAbort) return;
      const msg = err instanceof Error ? err.message : String(err);
      attemptsRef.current += 1;
      if (attemptsRef.current <= 2) {
        const backoffMs = attemptsRef.current === 1 ? 500 : 1500;
        setTimeout(() => {
          if (!signal.aborted) void runFetch(signal, true);
        }, backoffMs);
      } else {
        setError(msg);
      }
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [fetchAllInternal]);

  useEffect(() => {
    const key = `${platformServiceKey}::${provider ?? ''}::${readOnly ? 'ro' : 'rw'}`;
    if (lastKeyRef.current !== key) {
      lastKeyRef.current = key;
      attemptsRef.current = 0;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    void runFetch(ctrl.signal, false);
    return () => { ctrl.abort(); };
  }, [platformServiceKey, provider, readOnly, runFetch]);

  const handleRetry = useCallback(() => {
    attemptsRef.current = 0;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    void runFetch(ctrl.signal, false);
  }, [runFetch]);

  // Compat: helper usato dai CTA discovery/migrate per ricaricare.
  const fetchAll = handleRetry;

  const handleSelect = useCallback(async (modelId: string) => {
    if (!modelId || modelId === current) return;
    if (!provider) {
      notify({
        title: 'Provider non selezionato',
        description: 'Seleziona prima un provider per questo servizio.',
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      const url = buildSaveEndpoint
        ? buildSaveEndpoint(platformServiceKey)
        : `${platformConfigEndpoint}${platformServiceKey}`;
      // Task #2104 — payload canonico `{ provider, model }`. Il backend non
      // deriva più il provider dalla TSR / platform_model_config: lo legge
      // dal payload (sempre coerente con la select provider attiva nella UI).
      await apiClient.put(url, { provider, model: modelId });
      setCurrent(modelId);
      notify({
        title: 'Modello aggiornato',
        description: `${platformServiceKey} → ${modelId}`,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      notify({ title: 'Errore aggiornamento modello', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }, [current, platformServiceKey, provider, notify, apiClient, platformConfigEndpoint, buildSaveEndpoint]);

  // Task #2039 — gli handler `useCallback` qui sotto DEVONO stare PRIMA degli
  // early-return su `loading` / `error`, altrimenti il numero di hook
  // chiamati cambia tra i render (loading=true → 0 hook qui · loading=false
  // → 2 hook qui), causando React error #300/#310 al primo render con dati.
  const topSuggested = reco?.top?.[0] ?? null;

  // Task #2016 — CTA "Migra a top-1" per modello attuale fuori catalog.
  const handleMigrateToTop = useCallback(async () => {
    if (!topSuggested) return;
    await handleSelect(topSuggested.model_id);
  }, [topSuggested, handleSelect]);

  // Task #2016 — CTA "Esegui discovery" per allineare il catalog ed eventualmente
  // riportare in lista il modello attuale (o suggerimenti nuovi).
  const handleDiscovery = useCallback(async () => {
    setDiscovering(true);
    try {
      // Task #2016 — recovery deterministico per-provider: solo i provider
      // canonici (openai/google/claude) sono supportati dal backend
      // `POST /admin/models/discover/:provider`. Per provider custom non c'è
      // discovery: il catalog tenant-scoped è curato a mano dall'endpoint.
      const { provider_id } = resolveRecommendProvider(provider);
      if (!provider_id || provider_id === 'custom') {
        notify({
          title: 'Discovery non disponibile',
          description: 'I provider custom non hanno discovery automatica. Aggiungi il modello manualmente nella scheda Custom Endpoints.',
          variant: 'destructive',
        });
        return;
      }
      await apiClient.post(`${discoverEndpoint}/${provider_id}`, {});
      notify({ title: 'Discovery avviata', description: `Catalog ${provider_id} in aggiornamento, ricarico…` });
      await fetchAll();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      notify({ title: 'Discovery fallita', description: msg, variant: 'destructive' });
    } finally {
      setDiscovering(false);
    }
  }, [apiClient, discoverEndpoint, provider, notify, fetchAll]);

  if (readOnly) {
    return (
      <div className="flex flex-col gap-0.5" data-testid={`model-picker-readonly-${platformServiceKey}`}>
        {label && <span className="text-[10px] uppercase tracking-wide text-gray-500">{label}</span>}
        <TooltipProvider>
          <UITooltip>
            <TooltipTrigger asChild>
              <div
                className="h-7 flex items-center gap-1.5 px-2 rounded border border-gray-200 bg-gray-50 dark:bg-gray-900 dark:border-gray-700 text-xs cursor-help"
                style={{ width: widthPx }}
              >
                {loading ? (
                  <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
                ) : (
                  <span className="font-mono truncate flex-1 min-w-0 text-gray-700 dark:text-gray-200">
                    {current ?? '—'}
                  </span>
                )}
                <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0 border-gray-300 text-gray-500">
                  platform
                </Badge>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-[11px]">
                {readOnlyReason ?? 'Configurazione gestita a livello piattaforma. Modificabile solo da super admin.'}
              </p>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </div>
    );
  }

  if (loading && !reco) {
    return (
      <div className="flex items-center gap-1 text-[11px] text-gray-500" data-testid={`model-picker-loading-${platformServiceKey}`}>
        <Loader2 className="h-3 w-3 animate-spin" />
        modelli…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-amber-700" data-testid={`model-picker-error-${platformServiceKey}`}>
        <AlertTriangle className="h-3 w-3" />
        <span className="truncate" title={error}>recommender KO</span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-5 px-1.5 text-[10px] gap-1"
          onClick={(e: ReactMouseEvent<HTMLButtonElement>) => { e.preventDefault(); e.stopPropagation(); void fetchAll(); }}
          data-testid={`model-picker-retry-${platformServiceKey}`}
        >
          <RefreshCw className="h-3 w-3" />
          Riprova
        </Button>
      </div>
    );
  }

  // Nasconde i modelli deprecati dalle raccomandazioni, ma mantiene sempre il
  // modello attualmente selezionato (col badge "deprecated") così la scelta
  // corrente non sparisce dal dropdown.
  const top = (reco?.top ?? []).filter((e) => !e.is_deprecated || e.model_id === current);
  const others = (reco?.others ?? []).filter((e) => !e.is_deprecated || e.model_id === current);
  const allEntries = [...top, ...others];
  const currentInList = current ? allEntries.some((e) => e.model_id === current) : true;

  return (
    <div className="flex flex-col gap-0.5" data-testid={`model-picker-${platformServiceKey}`}>
      {label && <span className="text-[10px] uppercase tracking-wide text-gray-500">{label}</span>}
      <Select
        value={current ?? undefined}
        onValueChange={handleSelect}
        disabled={saving || allEntries.length === 0}
      >
        <SelectTrigger className="h-7 text-xs" style={{ width: widthPx }}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <SelectValue placeholder="Seleziona modello…" />}
        </SelectTrigger>
        <SelectContent className="max-h-[320px]">
          {top.length > 0 && (
            <div className="px-2 py-1 text-[9px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400 font-semibold">
              Top {top.length} consigliati
            </div>
          )}
          {top.map((e, idx) => renderItem(e, idx === 0 ? 'TOP' : null))}
          {!currentInList && current && (
            <>
              <SelectItem value={current} className="text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-mono">{current}</span>
                  <Badge variant="secondary" className="text-[9px]">attuale (fuori catalog)</Badge>
                </div>
              </SelectItem>
              {/* Task #2016 — CTA recovery inline per modello fuori catalog. */}
              <div className="px-2 py-1.5 border-t border-amber-200 bg-amber-50 dark:bg-amber-950/30 flex flex-wrap items-center gap-1.5" data-testid={`model-picker-recovery-${platformServiceKey}`}>
                {topSuggested && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-6 text-[10px] gap-1"
                    disabled={saving || discovering}
                    onClick={(e: ReactMouseEvent<HTMLButtonElement>) => { e.preventDefault(); e.stopPropagation(); void handleMigrateToTop(); }}
                    data-testid={`model-picker-migrate-${platformServiceKey}`}
                  >
                    <Wand2 className="h-3 w-3" />
                    Migra a {topSuggested.display_name || topSuggested.model_id}
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-6 text-[10px] gap-1"
                  disabled={discovering}
                  onClick={(e: ReactMouseEvent<HTMLButtonElement>) => { e.preventDefault(); e.stopPropagation(); void handleDiscovery(); }}
                  data-testid={`model-picker-discover-${platformServiceKey}`}
                >
                  {discovering ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  Esegui discovery
                </Button>
              </div>
            </>
          )}
          {others.length > 0 && (
            <div className="px-2 py-1 mt-1 text-[9px] uppercase tracking-wide text-gray-500 font-semibold border-t border-gray-200 dark:border-gray-700">
              Altri compatibili
            </div>
          )}
          {others.map((e) => renderItem(e, null))}
          {allEntries.length === 0 && (
            <div className="px-2 py-3 text-[11px] text-amber-700">
              Nessun modello compatibile per il provider <strong>{provider ?? '—'}</strong>.
              Verifica la capability + required_features di <code>{platformServiceKey}</code>.
            </div>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

function unwrap<T>(value: ApiEnvelope<T> | T | undefined): T {
  if (value && typeof value === 'object' && 'data' in (value as ApiEnvelope<T>)) {
    const env = value as ApiEnvelope<T>;
    if (env.data !== undefined) return env.data;
  }
  return value as T;
}

function renderItem(e: ScoredEntry, topBadge: 'TOP' | null) {
  type StatusBadge = { label: string; className: string; icon?: boolean };
  let status: StatusBadge | null = null;
  if (e.is_deprecated) {
    status = { label: 'deprecated', className: 'bg-red-100 text-red-700 border-red-200' };
  } else if (topBadge === 'TOP') {
    status = { label: 'TOP', className: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: true };
  } else if (e.is_new) {
    status = { label: 'NEW', className: 'bg-sky-100 text-sky-700 border-sky-200' };
  }
  return (
    <SelectItem key={`${e.provider}::${e.model_id}`} value={e.model_id} className="text-xs">
      <TooltipProvider>
        <UITooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2 w-full cursor-help">
              <span className="font-mono truncate flex-1 min-w-0">{e.display_name || e.model_id}</span>
              {status && (
                <Badge className={`text-[9px] px-1 py-0 shrink-0 ${status.className}`}>
                  {status.icon && <Sparkles className="h-2.5 w-2.5 mr-0.5 inline" />}
                  {status.label}
                </Badge>
              )}
              <span className="text-[10px] text-gray-400 shrink-0 tabular-nums">{e.score.toFixed(3)}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs">
            <p className="text-[11px] font-medium">
              <span className="text-gray-400 mr-1">{e.provider}</span>
              {e.motivation}
            </p>
            <ul className="mt-1 space-y-0.5 text-[10px] text-gray-300">
              <li>quality: {e.breakdown.quality.toFixed(2)}</li>
              <li>cost: {e.breakdown.cost.toFixed(2)}</li>
              <li>latency: {e.breakdown.latency.toFixed(2)}</li>
              <li>feature_match: {e.breakdown.feature_match.toFixed(2)}</li>
            </ul>
            {!e.metadata_complete && (
              <p className="mt-1 text-[10px] text-amber-300">metadata catalog incompleti</p>
            )}
            {e.is_new && !e.is_deprecated && topBadge === 'TOP' && (
              <p className="mt-1 text-[10px] text-sky-300">novità in catalog</p>
            )}
          </TooltipContent>
        </UITooltip>
      </TooltipProvider>
    </SelectItem>
  );
}

export default ServiceModelPicker;
